import { useState, useEffect, useCallback } from "react";
import { useScanData } from "../api";
import { DateTime } from "luxon";

const scanTypes = { "bulk-burst": "Raman", "trace-burst": "SERS" };

const DownloadData = ({ samples, needsScanData }) => {
  //state variables
  const [ids, setIds] = useState([]);
  const [data, setData] = useState([]);
  const [isDataUpdated, setIsDataUpdated] = useState(false);
  const [shouldSaveData, setShouldSaveData] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  //fetching scan data from database
  const scanQueries = useScanData(needsScanData ? ids : []);

  useEffect(() => {
    //check if samples is an array and set data accordingly
    if (!Array.isArray(samples)) {
      setData([samples]);
    } else {
      setData(samples);
    }

    //set ids array if needsScanData is true and samples is an array
    if (needsScanData && Array.isArray(samples)) {
      const sampleIds = samples.map((d) => d.sampleId);
      setIds(sampleIds);
    }
  }, [samples, needsScanData]);

  //updating data with scan data from database
  useEffect(() => {
    if (
      needsScanData &&
      !isDataUpdated &&
      scanQueries.length > 0 &&
      scanQueries.every((q) => q.isSuccess) &&
      isButtonClicked
    ) {
      setData((prevData) => {
        const newData = prevData.map((d) => {
          const sample = scanQueries.find(
            (q) => q.data?.sampleId === d.sampleId
          );
          return { ...d, tests: sample?.data?.tests };
        });
        setIsDataUpdated(true);
        setShouldSaveData(true);
        return newData;
      });
    }
  }, [scanQueries, needsScanData, isDataUpdated]);

  //calls saveData function when data is updated
  useEffect(() => {
    if (isDataUpdated && shouldSaveData) {
      saveData();
      setShouldSaveData(false);
      setIsButtonClicked(false);
    }
  }, [isDataUpdated, shouldSaveData]);

  const formatData = useCallback(() => {
    const headers = [
      "SampleID",
      "Date",
      "Time",
      "Food_Matrix",
      "Analyte",
      "Extraction_Solvent",
      "Findings",
    ];
    const sampleInfo = data?.map((d) =>
      d.tests
        ?.map((test) => {
          let localDate = DateTime.fromISO(d.processedDate).setZone("local");
          let scanType = scanTypes[test.scanType];
          let findings =
            test.findings?.map((f) => f.substance).join(", ") || "N/A";

          return [
            d.sampleId + "-" + scanType,
            localDate.toFormat("yyyy-MM-dd"),
            localDate.toFormat("HH:mm:ss"),
            d.foodMatrix,
            d.analyte,
            d.extractionSolvent,
            findings,
          ].join("\t");
        })
        .join("\n")
    );

    const infoData = headers.join("\t") + "\n" + sampleInfo.join("\n");

    const scanData =
      "\t" +
      (data[0]?.tests[0]?.wavenumber_plot || []).join("\t") +
      "\n" +
      data
        .map((d) =>
          d.tests
            ?.map((test) => {
              let scanType = scanTypes[test.scanType];
              return (
                d.sampleId +
                "-" +
                scanType +
                "\t" +
                (test.interpolatedRawDark || []).join("\t")
              );
            })
            .join("\n")
        )
        .join("\n");
    return { scanData, infoData };
  }, [data]);

  const saveAs = useCallback((blob, fileName) => {
    if (!(blob instanceof Blob)) {
      blob = new Blob([blob], { type: "text/tab-separated-values" });
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
  }, []);

  const fetchData = useCallback(async () => {
    if (needsScanData) {
      try {
        await Promise.all(
          scanQueries.map((query) => {
            if (query.refetch) {
              return query.refetch();
            }
            return Promise.resolve();
          })
        );
        setIsDataUpdated(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  }, [needsScanData, scanQueries]);

  const saveData = useCallback(() => {
    const { scanData, infoData } = formatData();
    saveAs(scanData, "data.txt");
    saveAs(infoData, "info.txt");
  }, [formatData, saveAs]);

  return (
    <div className='d-flex justify-content-center'>
      <button
        className='btn btn-outline-primary'
        onClick={() => {
          setIsButtonClicked(true);
          needsScanData ? fetchData() : saveData();
        }}
      >
        Download Data
      </button>
    </div>
  );
};

export default DownloadData;
